import { Injectable } from '@angular/core';
import { AppSettings } from 'app/app.settings';
import { ActivityService } from './activity.service';
import { AppinfoService } from './appinfo.service';
import { AttrIndexService } from './attr-index.service';
import { CommonRequestService, SuccessResponse } from './common-request.service';
import { LayerCategory, LayerCategoryStandardSchema } from './field-mapping.service';
import { AppLayer } from './layer.service';
import { UserService } from './user.service';

export type GivColumnDomain = {
	type: 'range';
	range: string; // '[x, x]', '(x, x)'...
} | {
	type: 'pattern';
	pattern: string; // regular expression
} | {
	type: 'list';
	list: any[];
}

export type DomainSimpleItem = string | number;

export interface NctDomainNestItem {
	value;
	children: {
		[childNctName: string]: (NctDomainNestItem | DomainSimpleItem)[];
	};
}
export interface NctDomainNest {
	[nctName: string]: NctDomainNestItem[];
}
export interface NctDomainChildren {
	[childNctName: string]: (NctDomainNestItem | DomainSimpleItem)[];
}

export interface NestedColumnPropRelation {
	parent?: string; // direct parent col prop
	children?: string[]; // direct child col prop
	nctName: string;
}



@Injectable({
  providedIn: 'root'
})
export class DomainService {

	nestedNctNameRelationIndex: {
		[nctName: string]: {
			parent?: string; // direct parent nct_name
			children?: string[]; // direct child nct_name
		};
	} = {};
	nestedColumnPropRelationIndex: {
		[colName: string]: NestedColumnPropRelation;
	};

	nestedDomainValueList: NctDomainNest = {};
	simpleDomainValueListByIndex: {
		[standardColName_or_nctName: string]: any[];
	} = {};



	// nenaDomains: NenaDomains = {}

	// placeNameDomains;

	// domainValueListByIndex: {
	// 	// For nena domain, idx is col_name
	// 	// For nct simple domain, idx is valueDomainNameNct
	// 	// For nct nested domain, idx is ...<grand parent valueDomainNameNct>-<grand parent value>-<parent valueDomainNameNcte>-<parent value>-<col name>
	// 	[idx: string]: any[];
	// } = {};
	// nestedNctNameRelationIndex: {
	// 	[nctName: string]: {
	// 		parent?: string; // direct parent nct_name
	// 		children?: string[]; // direct child nct_name
	// 	};
	// } = {};

	// indexSeparator = '-';

	constructor(
		private activityService: ActivityService,
		private appinfoService: AppinfoService,
		private attrIndexService: AttrIndexService,
		private requestService: CommonRequestService,
		private userService: UserService
	) { }

	async getDomains() {
		const customerInfo = this.userService.customerInfo;
		// Nena domain
		const rspMiscNena = await this.getNenaDomainMisc();
		rspMiscNena.data.domains.forEach(d => {
			const colName = d.col_name.toLowerCase();
			this.simpleDomainValueListByIndex[colName] = d.list;
		});
		// Nct domain
		const rspMisc = await this.getNagDomainsMisc();
		rspMisc.data.domains.forEach(d => {
			if (!Array.isArray(d.list)) {
				Object.keys(d.list).forEach(key => {
					const nctNameWithKey = d.nct_name + ':' + key;
					this.simpleDomainValueListByIndex[nctNameWithKey] = d.list[key];
				})
			} else {
				this.simpleDomainValueListByIndex[d.nct_name] = d.list;
			}
		});
		const rspJuris = await this.getNagDomainsJuris(customerInfo.givState);
		const handleJuris = (data, obj: NctDomainChildren, parentNctName?: string) => {
			if (!data.list) {
				return;
			}
			const nctName = data.nct_name;
			if (!Array.isArray(data.list)) {
				Object.keys(data.list).forEach(key => {
					const nctNameWithKey = data.nct_name + ':' + key;
					handleJuris({
						nct_name: nctNameWithKey,
						name: data.name,
						list: data.list[key]
					}, obj, parentNctName);
				});
				return;
			}
			if (!this.nestedNctNameRelationIndex[nctName]) {
				this.nestedNctNameRelationIndex[nctName] = {};
			}
			// Add parent relation
			if (parentNctName) {
				this.nestedNctNameRelationIndex[nctName].parent = parentNctName;
				// Add it as child relation for parent
				if (!this.nestedNctNameRelationIndex[parentNctName].children) {
					this.nestedNctNameRelationIndex[parentNctName].children = [nctName];
				} else if (this.nestedNctNameRelationIndex[parentNctName].children.indexOf(nctName) === -1) {
					this.nestedNctNameRelationIndex[parentNctName].children.push(nctName);
				}
			}
			if (data.list[0].nct_name) {
				//
				obj[nctName] = [];
				data.list.forEach(i => {
					const children: NctDomainChildren = {};
					obj[nctName].push({
						value: i.name,
						children: children
					});
					handleJuris(i, children, nctName);
				})
			} else {
				obj[nctName] = data.list;
			}

		}
		if (rspJuris.data) {
			this.nestedDomainValueList = {};
			handleJuris(rspJuris.data, this.nestedDomainValueList);
		}
	}
	private getNenaDomainMisc() {
		return new Promise<any>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nenaDomainsMisc';
			this.requestService.requestGet(url, {
				params: {
					customerId: this.userService.getCustomerIdByType()
				}
			}, (rsp) => {
				resolve(rsp);
			}, err => reject(err));
		});
	}
	private getNagDomainsJuris(stateCode, counties?: string[]) {
		return new Promise<SuccessResponse<any>>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagDomainsJuris';
			const params: any = {
				state: stateCode,
			};
			if (counties) {
				params.counties = JSON.stringify(counties);
			}
			this.requestService.requestGet(url, {
				params: params
			}, (rsp) => {
				resolve(rsp);
			}, err => reject(err));
		});
	}
	private getNagDomainsMisc() {
		return new Promise<any>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'admin/nagDomainsMisc';
			const params = {
				customerId: this.userService.getCustomerIdByType()
			}
			this.requestService.requestGet(url, {
				params: params
			}, (rsp) => {
				resolve(rsp);
			}, err => reject(err));
		});
	}

	getNagNeighbors(state: string, county?: string) {
		return new Promise<string[]>((resolve, reject) => {
			const url = AppSettings.serviceRootUrl + 'common/nagNeighbors';
			const params: any = {
				state: state
			};
			if (county) {
				params.county = county;
			}
			this.requestService.requestGet(url, {
				params: params
			}, rsp => {
				resolve(rsp.data.neighbors);
			}, err => reject(err));
		});
	}

	calcNestedColumnRelationIndex(cols: { prop: string; standard: LayerCategoryStandardSchema }[]) {
		this.nestedColumnPropRelationIndex = {};
		cols.forEach(col => {
			if (!col.standard || !col.standard.valueDomainNameNct) {
				return;
			}
			const nctName = col.standard.valueDomainNameNct;
			const domainRelation = this.nestedNctNameRelationIndex[nctName];
			if (!domainRelation) {
				return;
			}
			const relation: NestedColumnPropRelation = {
				nctName: nctName
			};
			const leftSuffix = '_l';
			const rightSuffix = '_r';
			let sideSuffix; // '_l', '_r'
			const find = (nctName, sideSuffix) => {
				const filtered = cols.filter(c => c.standard && c.standard.valueDomainNameNct === nctName);
				if (!sideSuffix) {
					return filtered[0];
				} else {
					const sideCol = filtered.find(c => c.prop.toLowerCase().endsWith(sideSuffix));
					return sideCol || filtered[0];
				}
			}
			if (col.prop.toLowerCase().endsWith(leftSuffix)) {
				sideSuffix = leftSuffix;
			} else if (col.prop.toLowerCase().endsWith(rightSuffix)) {
				sideSuffix = rightSuffix;
			}
			if (domainRelation.parent) {
				const parentCol = find(domainRelation.parent, sideSuffix);
				if (parentCol) {
					relation.parent = parentCol.prop;
				}
			}
			if (domainRelation.children) {
				relation.children = [];
				domainRelation.children.forEach(child => {
					const childCol = find(child, sideSuffix);
					if (childCol) {
						relation.children.push(childCol.prop);
					}
				});
			}
			this.nestedColumnPropRelationIndex[col.prop] = relation;
		})
	}

	getColumnDomains(cols: { prop: string; standard: LayerCategoryStandardSchema; }[], layerCateCode: string, rec?) {
		if (!this.nestedColumnPropRelationIndex || !Object.keys(this.nestedColumnPropRelationIndex).length) {
			this.calcNestedColumnRelationIndex(cols);
		}
		const domainByCol: {
			[colProp: string]: GivColumnDomain;
		} = {};
		const layerCate = this.appinfoService.layerTypeList.find(i => i.code === layerCateCode);
		const countyCol = (layerCate.colPrefix + '_' + this.attrIndexService.countyColNameStandard).toLowerCase();
		const countyLeftCol = countyCol + '_l';
		const countyRightCol = countyCol + '_r';
		if (!rec) {
			// Fake record with state and county
			rec = {}
			const stateCol = (layerCate.colPrefix + '_' + this.attrIndexService.stateColNameStandard).toLowerCase();
			const customerInfo = this.userService.customerInfo;
			const countyVal = customerInfo.cusCounties ? customerInfo.cusCounties[0] : null;
			const stateVal = customerInfo.givState;
			rec[countyCol] = countyVal;
			rec[stateCol] = stateVal;
			rec[countyLeftCol] = countyVal;
			rec[countyRightCol] = countyVal;
			rec[stateCol + '_l'] = stateVal;
			rec[stateCol + '_r'] = stateVal;
		}
		if (layerCate && layerCate.standardCols) {
			cols.forEach(col => {
				const standardCol = col.standard;
				if (!standardCol) {
					return;
				}
				let domain = this.getColumnDomain(col.prop, standardCol, layerCate, rec);
				if (domain) {
					if ((col.prop === countyCol || col.prop === countyLeftCol || col.prop === countyRightCol) && this.userService.customerInfo.cusCounties && this.userService.customerInfo.cusCounties.length && domain.type === 'list') {
						domain.list = domain.list.filter(i => this.userService.customerInfo.cusCounties.indexOf(i) > -1);
					}
					domainByCol[col.prop] = domain;
				}
			})
		}
		return domainByCol;
	}
	private getColumnDomain(colProp, standardCol: LayerCategoryStandardSchema, layerCate: LayerCategory, rec) {
		if (!layerCate) {
			return;
		}
		let domain: GivColumnDomain;
		const colPropWithoutPrefix = standardCol.colName.toLowerCase();
		let list = this.simpleDomainValueListByIndex[colPropWithoutPrefix];
		if (list) {
			domain = {
				type: 'list',
				list: list
			};
		}
		else if (standardCol.valueDomainNameNct) {
			const nctName = standardCol.valueDomainNameNct;
			list = list || this.simpleDomainValueListByIndex[nctName];
			if (!list && rec && this.nestedColumnPropRelationIndex[colProp]) {
				const tree: {
					nct: string;
					value;
				}[] = [];
				const addToTree = (prop) => {
					const colRelation = this.nestedColumnPropRelationIndex[prop];
					tree.unshift({
						nct: colRelation.nctName,
						value: rec[prop]
					});					
					if (colRelation.parent) {
						addToTree(colRelation.parent);
					}
				}
				addToTree(colProp);
				let nestedList: (NctDomainNestItem | DomainSimpleItem)[] = this.nestedDomainValueList[tree[0].nct];
				for (let i = 0; i < tree.length; i++) {
					if (!nestedList) {
						break;
					}
					const branch = tree[i];
					if (branch.nct === nctName) {
						list = typeof nestedList[0] === 'object' ? (<NctDomainNestItem[]>nestedList).map(i => i.value) : nestedList;
						break;
					} else {
						const next = (<NctDomainNestItem[]>nestedList).find(i => i.value === branch.value);
						if (!next) {
							break;
						}
						nestedList = next.children[tree[i + 1].nct];
					}
				}
			}
			if (!list) {
				list = [];
			}
			domain = {
				type: 'list',
				list: list
			};
		} else if (standardCol.valuePattern) {
			domain = {
				type: 'pattern',
				pattern: standardCol.valuePattern
			};
		} else if (standardCol.valueRange) {
			domain = {
				type: 'range',
				range: standardCol.valueRange
			}
		}
		return domain;
	}

	updateDependentDomains(domainsByColumn: {[prop: string]: GivColumnDomain}, changedCol: {prop: string; standardName: string}, rec, layerCateCode: string) {
		const layerCate = this.appinfoService.layerTypeList.find(i => i.code === layerCateCode);
		const standardCols = layerCate.standardCols;
		const standardCol = standardCols.find(c => c.colName === changedCol.standardName);
		if (!standardCol.valueDomainNameNct) {
			return;
		}
		const update = (nctName: string) => {
			if (this.nestedNctNameRelationIndex[nctName] && this.nestedNctNameRelationIndex[nctName].children) {
				this.nestedNctNameRelationIndex[nctName].children.forEach(childNctName => {
					const childCol = standardCols.find(c => c.valueDomainNameNct === childNctName);
					if (childCol) {
						const childColProp = (layerCate.colPrefix + '_' + childCol.colName).toLowerCase();
						domainsByColumn[childColProp] = this.getColumnDomain(childColProp, childCol, layerCate, rec);
						if (childCol.valueDomainNameNct) {
							update(childCol.valueDomainNameNct);
						}
					}
				});
			}
		}
		update(standardCol.valueDomainNameNct);
	}
}
